import React, { useState, useEffect, useCallback } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEqual } from 'lodash-es';

import SearchField from '../Search/SearchField';
import SelectedList from './components/SelectedList';
import SelectorList from './components/SelectorList';
import { getSearchMatches } from './utils';

const NestedSelector = ({
  onChange,
  options,
  selectedOptions,
  searchPlaceholder,
  showSelectectedList,
  showAsDisabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [opened, setOpened] = useState([]);

  useEffect(() => {
    let resetOpened = false;

    setSelected((prevSelected) => {
      if (isEqual(prevSelected, selectedOptions || [])) {
        return prevSelected;
      }

      resetOpened = !selectedOptions.length;
      return selectedOptions;
    });

    if (resetOpened) {
      setOpened(prevOpened => prevOpened.length ? [] : prevOpened);
    }
  }, [selectedOptions]);

  useEffect(() => {
    if (!searchQuery.length || searchQuery.length > 2) {
      const nextOpened = getSearchMatches(options, searchQuery);
      setOpened(nextOpened);
    }
  }, [searchQuery]);

  const handleSelectionChange = useCallback((selectedItems) => {
    setSelected(selectedItems);
    onChange(selectedItems);
  }, [onChange]);

  const handleUnselectOptions = useCallback((selectedItems) => {
    if (!selectedItems.length) setOpened([]);

    handleSelectionChange(selectedItems);
  }, [handleSelectionChange]);

  return (
    <div className={cx('nested-selector-container', { 'show-as-disabled': showAsDisabled })}>
      <SearchField
        placeholder={searchPlaceholder}
        size="sm"
        className="nested-selector-search-field"
        onChange={setSearchQuery}
      />
      {showSelectectedList && selected.length > 0 &&
        <SelectedList
          options={options}
          selected={selected}
          onSelectionChange={handleUnselectOptions}
        />
      }
      <SelectorList
        options={options}
        opened={opened}
        selected={selected}
        onOpenItemsChange={setOpened}
        onSelectionChange={handleSelectionChange}
      />
    </div>
  );
};

NestedSelector.propTypes = {
  onChange: T.func.isRequired,
  options: T.arrayOf(T.shape({
    value: T.oneOfType([T.string, T.number]).isRequired,
    text: T.node.isRequired,
    children: T.array,
  })).isRequired,
  selectedOptions: T.arrayOf(T.string),
  searchPlaceholder: T.string,
  showSelectectedList: T.bool,
  showAsDisabled: T.bool,
};

export default NestedSelector;


// Data structure example

// const options = [{
//   value: 'id',
//   text: 'Example',
//   children: [{
//     value: 'another_id',
//     text: 'Nested Example',
//     children: [
//       ... and so on and so forth
//     ],
//   }],
// }]
